// SEO constants. Keep BASE_URL absolute — used for schema @id, sitemap loc,
// og:url anchors. Relative paths still work for canonical/og:url at the
// route level; absolute URLs are required inside JSON-LD.

export const BASE_URL = "https://fenny.se";
export const BRAND = "Fenny";
export const ORG_ID = `${BASE_URL}/#organization`;
export const WEBSITE_ID = `${BASE_URL}/#website`;
export const LOCALE = "sv-SE";

// Canonical social share image (uppladdad i Lovable publish settings).
// Används som primär og:image/twitter:image på ALLA routes — leaves
// överstyr inte längre, så LinkedIn/Slack/X/FB alltid visar samma
// huvudbild oavsett vilken sida som delas.
export const SOCIAL_IMAGE_URL =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/xIFt8nCXc6NdCkUYNPysW4ahoQh2/social-images/social-1782195589070-Fenny_-_OG-featured_image.webp";
export const SOCIAL_IMAGE_ALT = "Fenny — Premiumfilm på abonnemang";
export const SOCIAL_IMAGE_WIDTH = "1200";
export const SOCIAL_IMAGE_HEIGHT = "630";

export const abs = (path: string) => {
  if (!path.startsWith("/")) return `${BASE_URL}/${path}`;
  return `${BASE_URL}${path}`;
};

// ---------------------------------------------------------------------------
// Verifierade profiler (sameAs). Endast konton som faktiskt existerar och
// tillhör rätt entitet får ligga här — fel sameAs bryter entitetskopplingen.
// Fenny och moderbolaget Norrhavet Group är SKILDA entiteter: Norrhavets
// kanaler hör hemma under parentOrganization, inte under Fenny.
// ---------------------------------------------------------------------------

/**
 * Fennys egna, verifierade profiler (sameAs-kedjan).
 * Fyll i en URL först när kontot är bekräftat och tillhör Fenny — tomma
 * värden filtreras bort helt, ett felaktigt sameAs bryter entitetskopplingen.
 */
export const FENNY_PROFILE_SLOTS: Record<string, string> = {
  linkedin: "https://www.linkedin.com/company/fenny-se/",
  youtube: "",
  instagram: "",
  facebook: "",
  vimeo: "",
  crunchbase: "",
  allabolag: "",
};

export const FENNY_PROFILES: string[] = Object.values(FENNY_PROFILE_SLOTS).filter(Boolean);

/** Moderbolagets profiler — exponeras via parentOrganization. */
export const PARENT_PROFILES: string[] = [
  "https://www.linkedin.com/company/norrhavetgroup",
  "https://www.instagram.com/norrhavet.group/",
  "https://www.youtube.com/@norrhavet",
  "https://www.facebook.com/norrhavetdigitalbyra",
];

/**
 * Juridisk identitet. Organisationsnumret är den starkaste
 * entitetsnyckeln i Sverige — det kopplar sajten till Bolagsverket,
 * allabolag och Googles Knowledge Graph. Formatet ska vara NNNNNN-NNNN.
 * Tomt tills numret bekräftats; vi gissar aldrig.
 */
export const ORG_NUMBER = "";

/**
 * Fenny är ett varumärke som drivs av Norrhavet Group AB. Det juridiska
 * organisationsnumret hör därför till moderbolaget och märks upp där —
 * inte på Fenny-noden.
 */
export const PARENT_LEGAL_NAME = "Norrhavet Group AB";
export const PARENT_ORG_NUMBER = "559258-3792";

/** Momsregistreringsnummer, SE + orgnr utan bindestreck + 01. Tomt tills bekräftat. */
export const VAT_ID = "";

/** identifier-noder för Organization-schemat. Utelämnas när värden saknas. */
export const organizationIdentifiers = () =>
  ORG_NUMBER
    ? [
        {
          "@type": "PropertyValue",
          propertyID: "Organisationsnummer (Bolagsverket)",
          value: ORG_NUMBER,
        },
      ]
    : [];

/**
 * Google Business Profile / Bing Places / Apple Business Connect.
 * TODO (kräver kontoåtkomst): fyll i när profilerna är verifierade.
 * Tomma värden utelämnas helt ur schemat — vi publicerar aldrig gissade URL:er.
 */
export const PLACE_PROFILES: Record<string, string> = {
  googleBusinessProfile: "",
  bingPlaces: "",
  appleBusinessConnect: "",
};

/** Alla ifyllda plats-profiler som en sameAs-array. */
export const placeProfileUrls = (): string[] =>
  Object.values(PLACE_PROFILES).filter((u) => Boolean(u));

// ---------------------------------------------------------------------------
// Entitetssignaler (AEO/GEO). AI-motorer väger entitetskonsistens tyngre än
// länkvolym: en Wikidata-post + konsekventa sameAs gör Fenny till en känd
// entitet istället för en okänd URL. Fyll i värden ENDAST när de är
// verifierade — tomma värden utelämnas helt ur schemat.
// ---------------------------------------------------------------------------

/** Wikidata-QID, t.ex. "Q123456789". Tomt tills posten är skapad och granskad. */
export const WIKIDATA_ID = "";

/** Wikidata-URL som sameAs, om QID är ifyllt. */
export const wikidataUrl = (): string[] =>
  WIKIDATA_ID ? [`https://www.wikidata.org/wiki/${WIKIDATA_ID}`] : [];

/** Bekräftade medlemskap i branschorganisationer (memberOf). */
export const MEMBERSHIPS: { name: string; url: string }[] = [];

/** Bekräftade utmärkelser (award). Endast verifierbara — aldrig påhittade. */
export const AWARDS: string[] = [];
